/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';

import { GmLogo } from '@/components/brand/GmLogo';
import { Icon } from '@/components/Icon';
import { cn } from '@/lib/cn';
import { prefetchRoute } from '@/routes/lazyRoutes';
import { useUiStore } from '@/stores/uiStore';
import { useCapabilities } from '@/lib/userRole';
import { navBottom, navTop, type NavItem } from './nav';
import { useRail } from './useRail';

/*
 * Rail entries name themselves by tooltip only while the rail is slim. `data-tip` rather than
 * `title`: the tooltip layer moves a `title` into `data-tip` itself the first time it shows, and
 * React could never take back an attribute it did not set.
 */

function RailButton({
  item,
  named,
  onPick,
}: {
  item: NavItem;
  named: boolean;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const remembered = useUiStore((s) => s.routeMemory[item.to]);
  const warm = () => prefetchRoute(item.to);
  // Restore last URL for this tool; query string doesn't affect NavLink active state.
  const to = remembered ?? item.to;
  const label = t(`nav.${item.key}`, item.label);
  return (
    <NavLink
      to={to}
      end={item.end}
      data-tip={named ? undefined : label}
      aria-label={label}
      data-tour={`nav-${item.key}`}
      onMouseEnter={warm}
      onFocus={warm}
      onClick={onPick}
      className={({ isActive }) => cn('rb', isActive && 'on')}
    >
      <Icon name={item.icon} size={22} />
      <span className="rb-label">{label}</span>
    </NavLink>
  );
}

/** Rail entry that opens a global dialog instead of navigating. */
function RailAction({
  item,
  named,
  onClick,
  active,
}: {
  item: NavItem;
  named: boolean;
  onClick: () => void;
  active?: boolean;
}) {
  const { t } = useTranslation();
  const label = t(`nav.${item.key}`, item.label);
  return (
    <button
      type="button"
      data-tip={named ? undefined : label}
      aria-label={label}
      data-tour={`nav-${item.key}`}
      onClick={onClick}
      className={cn('rb', active && 'on')}
    >
      <Icon name={item.icon} size={22} />
      <span className="rb-label">{label}</span>
    </button>
  );
}

/** Icon navigation rail; the arrow at its foot widens it to name every destination. */
export function Rail() {
  const { t } = useTranslation();
  const resourcesOpen = useUiStore((s) => s.resourcesOpen);
  const openResources = useUiStore((s) => s.openResources);
  const closeNav = useUiStore((s) => s.closeNav);
  const navOpen = useUiStore((s) => s.navOpen);
  const setRailPeek = useUiStore((s) => s.setRailPeek);
  const rail = useRail();
  const railRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const capabilities = useCapabilities();
  const closePeek = () => setRailPeek(false);

  // Open over the page, the rail goes the way a popover does: a pick, a click anywhere else,
  // Escape, or keyboard focus moving on into the page.
  useEffect(() => {
    if (!rail.over) return;
    const aside = railRef.current as HTMLElement;
    const close = () => setRailPeek(false);
    const onDown = (e: PointerEvent) => {
      if (!aside.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // Only a move to somewhere else: clicking the rail's own empty space blurs to nothing.
    const onFocusOut = (e: FocusEvent) => {
      const to = e.relatedTarget as Node | null;
      if (to && !aside.contains(to)) close();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    aside.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      aside.removeEventListener('focusout', onFocusOut);
    };
  }, [rail.over, setRailPeek]);

  // Phone drawer: focus moves in when it opens, Escape closes it, and focus goes back to whatever
  // opened it (the topbar burger) when it closes. The rest of the shell is inert meanwhile.
  useEffect(() => {
    if (!navOpen) return;
    const opener = document.activeElement as HTMLElement;
    (closeRef.current as HTMLButtonElement).focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeNav();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener.focus();
    };
  }, [navOpen, closeNav]);
  // Hide role-gated destinations (Analytics, Admin) the user can't reach.
  const visible = (item: NavItem) => !item.capability || capabilities.has(item.capability);
  const toggleLabel = rail.open
    ? t('nav.collapseSidebar', 'Collapse sidebar')
    : t('nav.expandSidebar', 'Expand sidebar');
  return (
    <aside ref={railRef} id="app-rail" className="rail" data-tour="rail">
      <div className="rail-logo">
        <GmLogo className="gm-badge" size={34} />
      </div>
      <button
        ref={closeRef}
        type="button"
        className="rail-close"
        title={t('common.close', 'Close')}
        aria-label={t('common.close', 'Close')}
        onClick={closeNav}
      >
        <Icon name="close" size={20} />
      </button>
      {navTop.filter(visible).map((item) =>
        item.key === 'resources' ? (
          <RailAction
            key={item.key}
            item={item}
            named={rail.open}
            onClick={() => {
              closeNav();
              closePeek();
              openResources();
            }}
            active={resourcesOpen}
          />
        ) : (
          <RailButton key={item.key} item={item} named={rail.open} onPick={closePeek} />
        ),
      )}
      <div className="rail-sp" />
      {navBottom.filter(visible).map((item) => (
        <RailButton key={item.key} item={item} named={rail.open} onPick={closePeek} />
      ))}
      <button
        type="button"
        className="rail-toggle"
        data-tip={toggleLabel}
        aria-label={toggleLabel}
        aria-expanded={rail.open}
        aria-controls="app-rail"
        onClick={rail.toggle}
      >
        <Icon name={rail.open ? 'arrow_back' : 'arrow_forward'} size={20} />
      </button>
    </aside>
  );
}
